import { Shape, ShapeType } from '@/services/shape';
import {
  addResourceIfNotExists,
  generateLayoutAst,
  insertImages
} from '../utils/resource.utils';
import { ImageAttribute } from '@/services/shape/attributes/image';
import { isSourceKeyFormat } from '@/utils/number';
import { ResourceKey } from '@/services/shape/attributes';
import { getRefName } from '../utils/name.utils';
import { buildAstState } from '..';
import generateID from '@/utils/generate-id';
import { round } from 'lodash-es';
import { getRealSourceKey } from '../utils/data-source.utils';
import { GlobalConfig } from '@/services/gloabl.config';
import { getPointerRotatedSize } from '../utils/pointer.utils';
import { canUse } from '@/utils/can-use.utils';
import { dataSourceList } from '@/resources/data-source';

const getShadowPointerOffset = (shape: Shape) => {
  // 获取第一个主题配色
  const firstColorId = buildAstState.data.theme?.colorGroupTable?.[0]?.id ?? '';

  // 获取第一个指针的大小
  const { width: pointerWidth = 0, height: pointerHeight = 0 } =
    shape.getImageSize('pointer0', firstColorId) ?? {};
  // 获取指针阴影大小
  const { width: shadowWidth = 0, height: shadowHeight = 0 } =
    shape.getImageSize('pointerShadow', firstColorId) ?? {};
  return {
    x: (shadowWidth - pointerWidth) / -2,
    y: (shadowHeight - pointerHeight) / -2,
    w: shadowWidth,
    h: shadowHeight
  };
};

const widgetPointerLayouts = (
  resourceAst: Ast,
  pointerLayouts: Ast[],
  shape: Shape
) => {
  // console.log('layouts', cloneDeep(pointerLayouts));

  const { x = 0, y = 0, w = 0, h = 0, name, pivotX, pivotY } = shape.attrs;
  const res: Ast[] = [];
  const _getOffset = (x2: number | string, x0: number | string) => {
    return Math.max(0, Number(x2) - Number(x0)).toString();
  };
  // 处理x y
  pointerLayouts.forEach((layout) => {
    const { x: x2, y: y2 } = layout.attrsMap;
    layout.attrsMap.x = _getOffset(x2, x);
    layout.attrsMap.y = _getOffset(y2, y);
    layout.tag = 'Item';
  });

  // 将layouts按每10个分成一组
  const pointerLayouts2: Ast[][] = [];
  const countPerWidget = GlobalConfig.widgetChildrenMaxCount;
  for (let i = 0; i < pointerLayouts.length; i += countPerWidget) {
    pointerLayouts2.push(pointerLayouts.slice(i, i + countPerWidget));
  }
  // 指针扫过的范围
  const rotatedSize = getPointerRotatedSize({ w, h, pivotX, pivotY });
  pointerLayouts2.forEach((layouts) => {
    const widgetAst: Ast = {
      tag: 'Widget',
      attrsMap: {
        widgetName: name ?? '',
        groupType: 'general',
        w: String(rotatedSize.w),
        h: String(rotatedSize.h),
        _label: '用于逆光源指针组合',
        _asWidget: 'pointer'
      },
      children: [...layouts]
    };
    const ast = addResourceIfNotExists(resourceAst, widgetAst);

    const layoutAst: Ast = generateLayoutAst({
      ref: '@' + ast.attrsMap.name,
      x,
      y
    });
    res.push(layoutAst);
  });

  return res;
};

const _getImageAsts = (resourceAst: Ast, shape: Shape) => {
  const attrs = shape.attrs as ImageAttribute;
  const {
    resources,
    recolorEnable,
    shadowPointerOffsetY = 0,
    format,
    scale = 1,
    alpha = 1
  } = attrs;

  // 全局缩放 × 元素自身缩放
  const themeScale = buildAstState.data?.theme?.scale ?? 1;
  const effectiveScale = themeScale * scale;

  // 单图
  // 添加图片
  const imageMap = insertImages(
    resourceAst,
    {
      pointer0: resources['pointer0']
    },
    {
      recolorEnable: recolorEnable ?? false,
      imgFormat: format,
      scale: effectiveScale,
      alpha
    }
  );
  // 生成 layout ast
  const layoutAsts: Ast[] = [];

  // 编辑器内部时需要添加指针阴影图
  if (buildAstState.options.buildFor == 'editor') {
    const pointerShadow = getRefName(imageMap['pointerShadow']);
    if (pointerShadow) {
      const shadow = generateLayoutAst({
        ref: getRefName(imageMap['pointerShadow']),
        x: attrs.x,
        y: attrs.y + shadowPointerOffsetY
      });
      shadow.attrsMap._id = generateID();
      layoutAsts.push(shadow);
    }
  }

  const layout = generateLayoutAst({
    ref: getRefName(imageMap['pointer0']),
    x: attrs.x,
    y: attrs.y
  });

  layoutAsts.push(layout);

  return layoutAsts;
};
const _getPointerAsts = (
  resourceAst: Ast,
  shape: Shape,
  siblingAsts: Ast[] | undefined,
  parent: StoreLayerItem | undefined
) => {
  const attrs = shape.attrs as ImageAttribute;
  const {
    pointerRegionNum = 1,
    resources,
    recolorEnable,
    source = '',
    pointerFps = 1,
    shadowPointerOffsetY = 0,
    format,
    _hideWhenOutRange,
    pivotX,
    pivotY,
    compassFps = 50
  } = attrs;

  // 指针
  const imageMap = insertImages(resourceAst, resources, {
    recolorEnable: recolorEnable ?? false,
    imgFormat: format
  });
  if (shape.isMultiUnitSource) {
    console.error('指针类型不可使用多单位数据源');
    return;
  }
  const { angleEnd = 0, angleStart = 0, valueEnd = 0, valueStart = 0 } = attrs;

  const pointerPivotX = pivotX ?? (attrs?.w ?? 0) / 2;
  const pointerPivotY = pivotY ?? (attrs?.h ?? 0) / 2;

  // valueStart和valueEnd是否使用了数据源
  const isUsingSourceDataValue =
    isSourceKeyFormat(valueStart) || isSourceKeyFormat(valueEnd);
  const baseRange = {
    angleStart: Number(angleStart),
    angleRange: Number(angleEnd) - Number(angleStart),
    valueStart: Number(valueStart),
    valueRange: Number(valueEnd) - Number(valueStart)
  };

  // 如果使用了数据源作为起始和结束值
  const baseRange2 = {
    ...baseRange,
    valueStart: isSourceKeyFormat(valueStart)
      ? getRealSourceKey(valueStart.toString())
      : String(valueStart),
    valueRange: isSourceKeyFormat(valueEnd)
      ? getRealSourceKey(valueEnd.toString())
      : String(Number(valueEnd) - Number(valueStart))
  };

  const layouts: Ast[] = [];
  const _addPointer = (
    pointerName: ResourceKey,
    range: {
      angleStart: string | number;
      angleRange: string | number;
      valueStart: string | number;
      valueRange: string | number;
    },
    shadowConfig?: {
      offsetX?: number;
      offsetY?: number;
      pivotX?: number;
      pivotY?: number;
    },
    renderRule?: 'alwaysShow' | 'hideWhenOutRange'
  ) => {
    if (!imageMap[pointerName]) return;

    const _pivotX = shadowConfig?.pivotX ?? pivotX ?? (attrs?.w ?? 0) / 2;
    const _pivotY = shadowConfig?.pivotY ?? pivotY ?? (attrs?.h ?? 0) / 2;
    const deviceLightSource = canUse('pointer-light-source').valid;
    let curRenderRule;
    if (deviceLightSource) {
      curRenderRule = attrs.renderRule
        ? attrs.renderRule
        : renderRule ??
          ((pointerRegionNum ?? 0) < 2
            ? _hideWhenOutRange
              ? 'hideWhenOutRange'
              : 'alwaysShow'
            : 'hideWhenOutRange');
    } else {
      curRenderRule = attrs.renderRule
        ? attrs.renderRule
        : attrs._hideWhenOutRange
        ? 'hideWhenOutRange'
        : 'alwaysShow';
    }
    // 添加上图片数字或图片数值对
    const refAst: Ast = {
      tag: 'DataItemPointer',
      attrsMap: {
        source: getRealSourceKey(source),
        ref: getRefName(imageMap[pointerName]),
        pivotX: String(_pivotX),
        pivotY: String(_pivotY),
        supportRecolor: String(attrs.recolorEnable ?? false),
        renderRule: curRenderRule,
        angleStart: String(range.angleStart),
        angleRange: String(range.angleRange),
        valueStart: String(range.valueStart),
        valueRange: String(range.valueRange)
      },
      children: []
    };

    if (source == 'timeSecond' && pointerFps) {
      refAst.attrsMap['parameter'] = String(round(1000 / pointerFps, 1));
    } else if (
      buildAstState.data.description?.deviceType === 'O61' &&
      source == 'systemSensorCompass' &&
      compassFps
    ) {
      refAst.attrsMap['parameter'] = String(round(1000 / compassFps, 1));
    }

    const ast = addResourceIfNotExists(resourceAst, refAst);

    // 生成 layout ast
    let _x = attrs.x + (shadowConfig?.offsetX ?? 0);
    let _y = attrs.y + (shadowConfig?.offsetY ?? 0);

    // 防止在Widget下元素出现负数
    if (parent?.type == ShapeType.Widget) {
      _x = Math.max(0, _x);
      _y = Math.max(0, _y);
    }

    const layoutAst: Ast = generateLayoutAst({
      ref: '@' + ast.attrsMap.name,
      x: _x,
      y: _y
    });

    // 如果是阴影，则添加随机的_id
    if (pointerName == 'pointerShadow') {
      layoutAst.attrsMap._id = shape.id + '_pointerShadow';
    }

    layouts.push(layoutAst);
  };

  const _getQuadrantIndex = (perAngle: number, angleStart: number) => {
    let _angleStart = angleStart % 360;
    const isLessThan0 = _angleStart < 0;
    if (isLessThan0) {
      _angleStart += 360;
    }
    const n = Math.floor(_angleStart / perAngle) + 1;
    let angleEnd = angleStart + 1;
    while (angleEnd % perAngle != 0) {
      angleEnd++;
    }

    return {
      n,
      angleEnd
    };
  };

  // 首先添加指针阴影
  const {
    // 阴影图片相对于指针图片偏移量
    x: offsetX,
    y: offsetY
    // 阴影图片的大小
    // w: shadowWidth,
    // h: shadowHeight
  } = getShadowPointerOffset(shape);

  _addPointer(
    'pointerShadow',
    isUsingSourceDataValue ? baseRange2 : baseRange,
    {
      // 指针阴影相对于指针的偏移量
      offsetX,
      offsetY: offsetY + shadowPointerOffsetY,
      // 指针阴影的旋转中心
      pivotX: pointerPivotX - offsetX,
      pivotY: pointerPivotY - offsetY
    }
  );
  const deviceLightSource = canUse('pointer-light-source').valid;

  // 如果使用了数据源，则忽略逆光源，只能添加第一个指针
  if (isUsingSourceDataValue) {
    _addPointer('pointer0', baseRange2, undefined, 'alwaysShow');
  } else if (
    pointerRegionNum <= 1 ||
    (pointerRegionNum >= 2 && !deviceLightSource)
  ) {
    // 指针象限为1 || 不可使用逆光缘的设备不添加多象限指针
    _addPointer('pointer0', baseRange);
  } else if (pointerRegionNum >= 2 && deviceLightSource) {
    // 使用2象限或4象限
    const angleRange = angleEnd - angleStart;
    const _perAngle = (angleRange < 360 ? angleRange : 360) / pointerRegionNum;
    // const totalN =
    //   Math.floor(baseRange.angleRange / _perAngle) +
    //   (baseRange.angleRange % _perAngle ? 1 : 0);
    // const _perValue = baseRange.valueRange / totalN;
    const _perAngleValue = baseRange.valueRange / baseRange.angleRange;

    // 当前第几象限
    let _angleStart = angleStart;
    let _angleEnd = Infinity;
    let _valueStart = Number(valueStart);
    let _valueEnd = Infinity;
    let resourceKey: undefined | ResourceKey;
    let count = 0;
    while (_angleStart < angleEnd && count < 9000) {
      // const n = Math.floor(_angleStart / _perAngle) + 1;
      const { n, angleEnd: __angleEnd } = _getQuadrantIndex(
        _perAngle,
        _angleStart
      );
      _angleEnd = Math.min(angleEnd, __angleEnd);
      _valueEnd = _valueStart + (_angleEnd - _angleStart) * _perAngleValue;
      _valueEnd = round(_valueEnd, 2);
      resourceKey = ('pointer' + ((n - 1) % pointerRegionNum)) as ResourceKey;
      // console.log(
      //   '象限',
      //   n,
      //   '角度',
      //   _angleStart,
      //   _angleEnd,
      //   '值',
      //   _valueStart,
      //   _valueEnd
      // );
      if (resourceKey) {
        _addPointer(resourceKey, {
          angleStart: _angleStart,
          angleRange: _angleEnd - _angleStart,
          valueStart: _valueStart,
          valueRange: round(_valueEnd - _valueStart, 2)
        });

        _valueStart = _valueEnd;
        _angleStart = _angleEnd;
        // 防止代码错误进入死循环
        count++;
      }
    }
    const sourceItem = Object.entries(dataSourceList)
      .map((i) => i[1])
      .flat()
      .find((i) => i.key === source);
    // 当超过范围时，需要显示最后一个指针，特增加一个超过范围的指针；非标准数据不增加这个指针
    if (
      resourceKey &&
      Number(valueEnd) - Number(valueStart) ==
        Number(sourceItem?.maxValue) - Number(sourceItem?.minValue)
    ) {
      _addPointer(resourceKey, {
        angleStart: angleEnd,
        // 确保旋转角度再最后一个角度上
        angleRange: 0,
        valueStart: _valueStart,
        // valueStart+valueRange的值需要比数据源返回的最大值大一些（这里特意+1）
        valueRange: (shape.sourceItem?.maxValue ?? 0) - Number(valueEnd) + 1
      });
    }
  }
  // widget下有最大数量限制，这里用widget包裹一下，规避这个问题
  const parentWidget = parent;
  if (
    parentWidget?.type == ShapeType.Widget &&
    buildAstState.options.buildFor == 'export'
  ) {
    // 获取有多少ids
    const currLen = siblingAsts?.length ?? 0;
    const ids = [
      ...new Set(siblingAsts?.map((item) => item.attrsMap._id) ?? [])
    ];
    const maxLen = GlobalConfig.widgetChildrenMaxCount ?? 10;
    const extraLenTotal = maxLen - (parentWidget?.children?.length ?? 0);
    const extraLenUsed = currLen - ids.length;
    const extraLenLeft = extraLenTotal - extraLenUsed;
    const extraLenNeed = layouts.length ?? 0;
    if (extraLenNeed > extraLenLeft) {
      return widgetPointerLayouts(resourceAst, layouts, shape);
    }
  }
  return layouts;
};

export const getImageAst = (
  resourceAst: Ast,
  shape: Shape,
  siblingAsts: Ast[] | undefined,
  parent: StoreLayerItem | undefined
) => {
  return shape.isPointerImage
    ? _getPointerAsts(resourceAst, shape, siblingAsts, parent)
    : _getImageAsts(resourceAst, shape);
};
